import * as Linking from 'expo-linking';
import { getStateFromPath as RNGetStateFromPath, getPathFromState as RNGetPathFromState } from '@react-navigation/native';
import { Platform } from 'react-native';
import { HOST } from '@api/constant';
import { extractProfileUserIdFromIncomingUrl } from '@utils/extractProfileUserIdFromIncomingUrl';
import { isDashboardPath } from '@utils/hasPersistedWebSession';
import {
	buildEasyModeNavigationState,
	extractEasyModePromptFromPath,
	isEasyModeHelpDeepLinkPath,
	stashMarketingPromptFromPath,
	stashPendingEasyModeDeepLink,
} from '@navigation/screens/MidLevel/EasyModeScreen/utils/easyModeDeepLink';
import {
	consumePendingDashboardDeepLink,
	stashPendingDashboardDeepLink,
} from '@navigation/utils/pendingDashboardDeepLink';

const isWeb = Platform.OS === 'web';

let initialWebPath: string | null =
	isWeb && typeof window !== 'undefined'
		? `${window.location.pathname}${window.location.search}`
		: null;

// Must run before getStateFromPath sends `/` and `/login` to Loading, which drops the query string.
if (initialWebPath) {
	stashMarketingPromptFromPath(initialWebPath);
}

/** True once, for the URL the page was loaded with. */
function takeInitialWebPath(path: string): boolean {
	if (initialWebPath === null || path !== initialWebPath) return false;
	initialWebPath = null;
	return true;
}

function isStorePath(path: string): boolean {
	return path === '/store' || path.startsWith('/store/') || path.startsWith('/store?');
}

/** Web storefront is served by the backend (Next.js) on HOST, not by this app. */
function redirectToHostedStore(path: string): boolean {
	if (typeof window === 'undefined' || !HOST) return false;
	try {
		const target = new URL(path, HOST);
		if (target.origin === window.location.origin) return false;
		window.location.replace(target.toString());
		return true;
	} catch {
		return false;
	}
}

/** Web: /auth, /auth/login, /auth/learn… are legacy aliases for the unprefixed auth URLs. */
function normalizeWebAuthPath(path: string): string {
	const match = path.match(/^\/auth(\/[^?]*)?(\?.*)?$/);
	if (!match) return path;
	const subPath = match[1] && match[1] !== '/' ? match[1] : '/login';
	return `${subPath}${match[2] ?? ''}`;
}

const UNSERIALIZABLE_QUERY_VALUES = new Set(['', '[object Object]', 'undefined', 'null']);

/** Object params (e.g. IntegrationDetail's `integration`) serialize to junk query values. */
function stripUnserializableQueryParams(path: string): string {
	const queryIndex = path.indexOf('?');
	if (queryIndex === -1) return path;
	const base = path.slice(0, queryIndex);
	const kept = path
		.slice(queryIndex + 1)
		.split('&')
		.filter((pair) => {
			if (!pair) return false;
			const eq = pair.indexOf('=');
			const rawValue = eq === -1 ? '' : pair.slice(eq + 1);
			let value = rawValue;
			try {
				value = decodeURIComponent(rawValue);
			} catch {
				/* keep raw */
			}
			return !UNSERIALIZABLE_QUERY_VALUES.has(value);
		});
	return kept.length ? `${base}?${kept.join('&')}` : base;
}

const profileLinkPrefixes = Array.from(
	new Set(
		[HOST, 'https://api.smartsaas.pro', 'https://smartsaas.pro'].filter(
			(p): p is string => typeof p === 'string' && p.length > 0
		)
	)
);

//TODO: Add linking configuration for the working application for WEB URLs!
const linking = {
	prefixes: [Linking.createURL('/ow'), ...profileLinkPrefixes],
	getPathFromState(state, options) {
		const path = RNGetPathFromState(state, options);
		// Strip query params for Loading screen to avoid ?params=%5Bobject%20Object%5D on logout
		if (path.includes('loading') && path.includes('?')) {
			return path.replace(/\?.*$/, '');
		}
		return stripUnserializableQueryParams(path);
	},
	getStateFromPath(rawPath, options) {
		let path = rawPath;
		if (isWeb && typeof path === 'string') {
			path = normalizeWebAuthPath(path);
			const pathOnly = path.split('?')[0].replace(/\/+$/, '');
			// Root and bare /login go through Loading so a valid cookie session skips the login page.
			if (pathOnly === '' || pathOnly === '/login') {
				const hasLoginParams = /[?&](code|promoCode|productId)=/.test(path);
				if (pathOnly === '' || !hasLoginParams) {
					return { routes: [{ name: 'Loading' }] };
				}
			}
			if (isStorePath(path) && redirectToHostedStore(path)) {
				return { routes: [{ name: 'Loading', params: { flagHold: true } }] };
			}
		}

		// React Navigation also calls getStateFromPath on every state change (to check route.path),
		// so the stash-and-Loading redirect must only run for the page's initial URL.
		const isInitialWebParse = typeof rawPath === 'string' && takeInitialWebPath(rawPath);

		// Watch / Siri / Shortcuts: /help or /easy-mode?prompt=… → Easy Mode
		if (typeof path === 'string' && isEasyModeHelpDeepLinkPath(path)) {
			const prompt = extractEasyModePromptFromPath(path);
			if (isInitialWebParse) {
				// Cold load still goes through Loading for cookie session restore;
				// LoadingScreen consumes the stashed prompt after auth.
				stashPendingEasyModeDeepLink(prompt);
				return {
					routes: [{ name: 'Loading' }],
				};
			}
			return buildEasyModeNavigationState(prompt);
		}

		// Web: always route dashboard URLs through Loading on cold load/refresh so
		// getSession() can restore (or reject) the httpOnly server cookie before
		// mounting the dashboard shell. Persisted Redux alone is not enough.
		if (isInitialWebParse && typeof path === 'string' && isDashboardPath(path)) {
			stashPendingDashboardDeepLink(path);
			return {
				routes: [{ name: 'Loading' }],
			};
		}

		// Normalize store deep links for e2e sales tracing and checkout attribution.
		if (!isWeb && typeof path === 'string' && isStorePath(path)) {
			const [rawPath, queryPart] = path.split('?');
			const segments = rawPath.split('/').filter(Boolean);
			const params = new URLSearchParams(queryPart || '');
			const pathStoreId = segments[1];
			const storeId = params.get('storeId') || params.get('uid') || pathStoreId || undefined;
			const campaignId = params.get('campaignId') || params.get('cid') || undefined;
			const traceId = params.get('traceId') || params.get('salesTraceId') || params.get('e2eTraceId') || undefined;
			const e2eRoute = params.get('e2eRoute') || params.get('route') || undefined;

			return {
				routes: [
					{
						name: 'Store',
						state: {
							routes: [
								{
									name: 'StoreList',
									params: {
										storeId,
										uid: params.get('uid') || undefined,
										campaignId,
										traceId,
										e2eRoute,
										source: params.get('source') || params.get('utm_source') || undefined,
										medium: params.get('medium') || params.get('utm_medium') || undefined,
										content: params.get('content') || params.get('utm_content') || undefined,
										term: params.get('term') || params.get('utm_term') || undefined,
										utm_source: params.get('utm_source') || undefined,
										utm_medium: params.get('utm_medium') || undefined,
										utm_content: params.get('utm_content') || undefined,
										utm_term: params.get('utm_term') || undefined,
										referrer: params.get('referrer') || undefined,
										buyerName: params.get('buyerName') || undefined,
										buyerEmail: params.get('buyerEmail') || undefined,
										buyerPhone: params.get('buyerPhone') || undefined,
									},
								},
							],
						},
					},
				],
			};
		}

		// HTTPS /profiles/...?id= — Universal Link / App Link: do not map to a stack screen;
		// ProfileBusinessCardPortal opens the sheet from Expo Linking (same URL).
		if (typeof path === 'string' && extractProfileUserIdFromIncomingUrl(path)) {
			return undefined;
		}

		// Password setup (invite / initialisation) — must run before /auth affiliate handling
		if (
			typeof path === 'string' &&
			(path === '/set-password' ||
				path.startsWith('/set-password?') ||
				path === '/auth/set-password' ||
				path.startsWith('/auth/set-password?'))
		) {
			const queryPart = path.includes('?') ? path.split('?')[1] : '';
			const params = new URLSearchParams(queryPart || '');
			const setPasswordParams: Record<string, string> = {};
			const email = params.get('email');
			const validationCode = params.get('code');
			if (email) setPasswordParams.email = email;
			if (validationCode) setPasswordParams.code = validationCode;
			return {
				routes: [
					{
						name: 'Auth',
						state: {
							routes: [
								{
									name: 'SetPassword',
									params: setPasswordParams,
								},
							],
						},
					},
				],
			};
		}

		// Handle /login?code=... - preserve affiliate/promo code and productId in params
		if (
			typeof path === 'string' &&
			(path.includes('/login') || (path.includes('/auth') && !path.includes('/set-password'))) &&
			path.includes('?')
		) {
			const [pathPart, queryPart] = path.split('?');
			if (queryPart) {
				const params = new URLSearchParams(queryPart);
				const code = params.get('code') || params.get('promoCode');
				const productId = params.get('productId');
				if (code || productId) {
					const loginParams: Record<string, string> = {};
					if (code) loginParams.code = code;
					if (productId) loginParams.productId = productId;
					return {
						routes: [
							{
								name: 'Auth',
								state: {
									routes: [
										{
											name: 'Login',
											params: loginParams,
										},
									],
								},
							},
						],
					};
				}
			}
		}
		// Handle /learn route - map it to Auth stack with Learn screen
		// Check if path is exactly /learn or contains /learn
		if (
			typeof path === 'string' &&
			(path === '/learn' || path.includes('/learn'))
		) {
			// Return navigation state that routes to Auth > Learn
			const defaultState = RNGetStateFromPath(path, options);
			// Web always needs learningMode set; the default match would drop it.
			if (isWeb || defaultState?.routes?.[0]?.name !== 'Auth') {
				return {
					routes: [
						{
							name: 'Auth',
							state: {
								routes: [
									{
										name: 'Learn',
										params: { learningMode: true },
									},
								],
							},
						},
					],
				};
			}
		}
		// Use default path matching for other routes
		return RNGetStateFromPath(path, options);
	},
	config: {
		screens: {
			// Web root stack is only Loading / Auth / Dashboard / NotFound; these are native LoginStack routes.
			...(isWeb
				? {}
				: {
						Form: 'form/:id',
						LoadingScreen: 'loading-screen',
						UserProfile: 'userprofile/:userId',
						Login: 'login',
						Learn: 'learn',
						Registration: 'registration/:productId',
						SetPassword: 'set-password',
					}),
			Loading: 'loading',
			Auth: {
				path: isWeb ? '' : 'auth',
				screens: {
					Login: 'login',
					Learn: 'learn',
					Registration: 'registration/:productId',
					SetPassword: 'set-password',
					...(isWeb ? { Preview: 'preview' } : {}),
				},
			},
			Dashboard: {
				path: 'dashboard',
				screens: {
					EasyMode: {
						path: 'easy-mode',
						parse: {
							// Kept for React Navigation query passthrough; getStateFromPath
							// maps prompt|q|text|message → initialUserPrompt.
							prompt: (value: string) => value,
							q: (value: string) => value,
						},
					},
					Community: 'community',
					Welcome: 'welcome',
					Invitations: 'invitations',
					Messages: 'messages',
					Marketplace: 'marketplace',
					ComingSoonScreen: 'coming-soon',
					IntegrationDetail: {
						path: 'integrations/detail',
						parse: {
							integration: (id: string) => ({ _id: id }),
						},
						stringify: {
							integration: (integration: any) =>
								String(integration?._id || integration?.id || integration?.meta || ''),
							contextAuthentications: () => '',
						},
					},
					Data: {
						path: 'data',
						screens: {
							Data: 'data',
							Find: 'find',
							GenericDataItemScreen: 'item',
							Media: 'media',
							Upload: 'upload',
							ActionHandler: 'action-handler/:dataId',
							CreateData: 'create-data',
							ManageData: 'manage-data',
							DataStore: 'data-store',
							DocumentTemplateEditor: 'document-editor/:dataId',
							TimeLineData: 'timeline/:dataId',
							ComponentDesigner: 'component-designer/:dataId',
							DataCreator: 'data-creator',
							DataHandler: 'data-handler',
							DataBrowser: 'data-browser/:requestId/:title',
							DataTemplates: 'data-templates',
							DataMenu: 'data-menu',
							Utilities: 'utilities',
							CreateEmailTemplate: 'create-email-template',
							PublicData: 'public-data',
							WhatsappTemplates: 'whatsapp-templates',
							BubbleScreen: 'data-bubbles',
							PreviewEmailTemplate:
								'preview-email-template/:templateId',
							PreviewDocumentTemplate:
								'preview-document-template/:templateId',
							PreviewInvoiceTemplate:
								'preview-invoice-template/:templateId',
							EmailTemplates: 'email-templates',
							InvoiceTemplates: 'invoice-templates',
							Invoice: 'invoice/:templateId',
							WebpageTemplates: 'webpage-templates',
							PreviewWebpageTemplate: 'preview-webpage-template/:templateId',
							EditWebpageTemplate: 'edit-webpage-template/:templateId',
							AppSites: 'app-sites',
							SiteBuilder: 'app-builder/:siteId',
							DocumentTemplates: 'document-templates',
							ProductScreen: 'product/:contentId',
							ServiceScreen: 'service/:contentId',
							CreativeTemplates: 'creative-templates',
							NewCreativeTemplate: 'edit-creative-template',
							InvoiceDetail: 'invoice-detail/:invoiceId',
						},
					},
					Flows: {
						path: 'flows',
						screens: {
							FlowMenu: 'flows',
							ManualFlows: 'manual-flows',
							SalesFlows: 'sales-flows',
							CallFlows: 'call-flows',
							FlowDesigner: 'flow-designer/:flowId/:mode',
							FlowEditor: 'flow-editor/:flowId/:mode',
						},
					},
					Sales: {
						path: 'sales',
						screens: {
							SalesDashboard: 'sales-dash',
							PastSales: 'past-sales',
							SalesLogistics: 'fulfilment',
							GoogleAnalyticsSales: {
								path: 'google-analytics',
								screens: {
									AnalyticsHome: '',
									AnalyticsDomains: 'domains',
								},
							},
							GoogleBusinessReviews: 'google-reviews',
							SalesFunnel: 'sales-funnel/:campaignId',
							SalesStats: 'sales-stats/:id',
							SocialPosts: {
								path: 'social',
								screens: {
									Dashboard: 'dashboard',
									History: 'history',
									Schedule: 'schedule',
									Analytics: 'analytics',
									Designer: 'designer',
									Settings: 'settings',
								},
							},
							PaidOutreach: 'paid-outreach',
							ActiveCampaigns: 'active-campaigns',
							PreviousCampaigns: 'previous-campaigns',
							SalesLeads: 'sales-leads',
							Flows: 'flows',
							ManualFlows: 'manual-flows',
							SalesFlows: 'sales-flows',
							CallFlows: 'call-flows',
							FlowDesigner: 'flow-designer/:flowId/:mode',
							FlowEditor: 'flow-editor/:flowId/:mode',
						},
					},
					Projects: {
						path: 'projects',
						screens: {
							ProjectsList: 'project-list',
							IndividualProjectPage: 'project/:projectId/:title',
							ProjectMenu: 'project-menu',
							Templates:'project-templates'
						},
					},
					Reports: {
						path: 'data-reports',
						screens: {
							Reports: 'reports',
							ReportsList: 'reports-list',
							IndividualReportScreen: 'report/:dataId',
							ReportDashboard: 'report-dashboard',
							CustomDashboards: 'custom-dashboards',
							AIAnalytics: 'ai-analytics',
							SalesAnalytics: 'sales-analytics',
						},
					},
					Forecast: {
						path: 'forecast',
						screens: {
							ForecastsList: 'forecasts',
							BusinessPlans: 'business-plans',
							Analysis: 'analysis',
							Competitors: 'competitors',
							PlanEditor: 'plan-editor/:planId',
						},
					},
					Calendar: {
						path: 'calendar',
						screens: {
							MainCalendar: 'main-calendar',
							Reminders: 'reminders',
							Meetings: 'meetings',
							Bookings: 'bookings',
							Breakdown: 'breakdown',
						},
					},
					Academy: {
						path: 'academy',
						screens: {
							Completed: 'completed',
							Progress: 'progress',
							Courses: 'courses/:id',
							Certifications: 'certifications',
							AcademyMain: 'menu',
							CourseEditor: 'course-editor/:courseId/:mode',
						},
					},
					Users: {
						path: 'users',
						screens: {
							UserList: 'user-list',
							StartupBuilder: 'startup-builder',
							UserActivity: 'user-activity',
							UserManagement: 'user-management',
							CompanySettings: 'company-settings',
							LinkedOrganisations: 'linked-organisations',
							SharingScopes: 'sharing-scopes',
							AgentTasks: 'agent-tasks',
							AgentUsage: 'usage',
							AgentEditor: 'agent-editor/:agentId',
							HR: {
								path: 'hr',
								screens: {
									Dashboard: 'dashboard',
									Employees: 'employees',
									Leave: 'leave',
									Attendance: 'attendance',
									Payroll: 'payroll',
									Performance: 'performance',
									Resumes: 'resumes',
								},
							},
						},
					},
					Company: {
						path: 'company-hub',
						screens: {
							CompanyMenu: 'menu',
							Company: 'profile',
							CompanySettings: 'settings',
							CompanyArchive: 'archive',
							SharingScopes: 'sharing-scopes',
							LinkedOrganisations: 'linked-organisations',
							AgentUsage: 'usage',
						},
					},
					Integrations: 'integrations',
					Settings: {
						path: 'settings',
						screens: {
							Profile: 'profile',
							Achievements: 'achievements',
							Upgrade: 'upgrade',
							API: 'api',
							AIModelKeys: 'ai-model-keys',
							Context: 'context',
							StoreSettings: 'store-settings',
							AccountDeletion: 'account-deletion',
							Language: 'language',
							CompanyMenu: 'company-menu',
							AgentUsage: 'usage',
							Company: 'company',
							SettingsMenu: 'settings-menu',
							Credits: 'credits',
							Statistics: {
								path: 'statistics',
								screens: {
									UserStatistics: 'user-statistics',
									AIStatistics: 'ai-statistics',
								},
							},
							Affiliate: 'affiliate',
							RegulatoryWatch: 'regulatory-watch',
							NotificationLogs: 'notification-logs',
							OfflineQueue: 'offline-queue',
							RecycleBin: 'recycle-bin',
							HelpAndTutorials: 'help-and-tutorials',
						},
					},
					Knowledge: {
						path: 'knowledge',
						screens: {
							ChatBot: 'chat-bot',
							InternalKnowledge: 'documents',
							Decks: 'decks',
							ExternalKnowledge: 'external-knowledge',
							KnowledgeScreen: 'knowledge-screen',
							EditKnowledge: 'edit-knowledge/:knowledgeArticleId',
							JournalEntry: 'journal-entry',
							UserUploadedDocuments: 'user-uploaded-documents',
							MonitoredUrls: 'monitored-urls',
						},
					},
					Mail: {
						path: 'mail',
						screens: {
							MailLoginScreen: 'login',
							Main: 'mailbox',
							ComposeMail: 'compose',
						},
					},
					Networking: {
						path: 'networking',
						screens: {
							Map: 'map',
							Profiles: 'profiles',
							MeetingList: 'meeting-list',
							Search: 'search',
							Meet: 'meet',
						},
					},
					Help: 'help',
				},
			},
			...(isWeb
				? {}
				: {
						Store: {
							path: 'store',
							screens: {
								StoreLoading: 'loading-store',
								StoreList: 'store/:storeId',
							},
						},
					}),
			NotFound: '404',
		},
	},
	// 		getStateFromPath(path, options) {
	// 			let state = getStateFromPath(path, options);
	// console.log(state)
	// 			// If the state is undefined, it means that the path doesn't match any route in our config, and
	// 			// we want to handle the path ourselves to the right screen, by updating the state and returning it.
	// 			// if (!state) {
	// 			// 	// check if route contains the main identifier of the screen we want to show
	// 			// 	const isTree = path.includes('dashboard');
	// 			// 	if (isTree) {
	// 			// 		const [, , branch, ...rest] = path.split('/');

	// 			// 		state = {
	// 			// 			routes: [
	// 			// 				{
	// 			// 					path,
	// 			// 					name: 'Dashboard',
	// 			// 					params: {
	// 			// 						branch,
	// 			// 						path: rest.join('/'), // here, our path is the rest of the path after the branch
	// 			// 					},
	// 			// 				},
	// 			// 			],
	// 			// 		};
	// 			// 	}
	// 			// }
	// 			return state;
	// 		},
};

export type PendingDashboardRoute =
	| { kind: 'route'; route: { name: 'Dashboard'; state: any } }
	| { kind: 'notFound' };

/**
 * Resolve the dashboard URL stashed during a web cold load into a root `Dashboard` route
 * (with nested state) that can be passed to CommonActions.reset. Returns null when nothing
 * was stashed or the path points at the default dashboard screen.
 */
export function consumePendingDashboardRoute(): PendingDashboardRoute | null {
	const path = consumePendingDashboardDeepLink();
	if (!path) return null;
	const state = RNGetStateFromPath(path, linking.config as any);
	const dashboardRoute = state?.routes?.[0];
	if (!dashboardRoute || dashboardRoute.name !== 'Dashboard') {
		return { kind: 'notFound' };
	}
	const nested = dashboardRoute.state;
	if (!nested?.routes?.length || nested.routes[0]?.name === 'EasyMode') {
		return null;
	}
	return { kind: 'route', route: { name: 'Dashboard', state: nested } };
}

export default linking;
